import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import Button from "../../../components/ui/Button";
import Icon from "../../../components/AppIcon";
import {
  fadeInUp,
  fadeInDown,
  fadeInLeft,
  fadeInRight,
  staggerContainer,
  staggerItem,
  floatingAnimation,
  usePageLoadAnimation,
} from "../../../utils/animations";

const HeroSection = () => {
  const [currentMetric, setCurrentMetric] = useState(0);
  const [animatedCounts, setAnimatedCounts] = useState({
    businesses: 0,
    revenue: 0,
    rating: 0,
  });
  const isPageLoaded = usePageLoadAnimation();

  const metrics = [
    { label: "Businesses Managed", value: 5000, suffix: "+", prefix: "" },
    { label: "Sales Processed", value: 50, suffix: "M+", prefix: "$" },
    { label: "App Rating", value: 4.8, suffix: "/5", prefix: "" },
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentMetric((prev) => (prev + 1) % metrics?.length);
    }, 3000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const animateCount = (target, key) => {
      let start = 0;
      const increment = target / 50;
      const timer = setInterval(() => {
        start += increment;
        if (start >= target) {
          start = target;
          clearInterval(timer);
        }
        setAnimatedCounts((prev) => ({ ...prev, [key]: Math.floor(start) }));
      }, 30);
    };

    animateCount(5000, "businesses");
    animateCount(50, "revenue");
    animateCount(48, "rating");
  }, []);

  const handleTrialClick = () => {
    const trialSection = document.getElementById("trial-signup");
    if (trialSection) {
      trialSection?.scrollIntoView({ behavior: "smooth" });
    }
  };

  const handleDemoClick = () => {
    const demoSection = document.getElementById("demo-section");
    if (demoSection) {
      demoSection?.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <motion.section
      className="relative min-h-screen bg-gradient-to-br from-white via-gray-50 to-blue-50 pt-20 lg:pt-24 overflow-hidden"
      initial="hidden"
      animate={isPageLoaded ? "visible" : "hidden"}
      variants={staggerContainer}
    >
      {/* Animated Background Elements */}
      <div className="absolute inset-0 overflow-hidden">
        <motion.div
          className="absolute top-20 right-10 w-72 h-72 bg-primary/5 rounded-full blur-3xl"
          variants={floatingAnimation}
          animate="animate"
        ></motion.div>
        <motion.div
          className="absolute bottom-20 left-10 w-96 h-96 bg-secondary/5 rounded-full blur-3xl"
          variants={floatingAnimation}
          animate="animate"
          transition={{ delay: 1 }}
        ></motion.div>
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-20">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* Left Content */}
          <motion.div
            className="text-center lg:text-left"
            variants={staggerContainer}
            initial="hidden"
            animate="visible"
          >
            <motion.div
              className="inline-flex items-center px-4 py-2 bg-primary/10 backdrop-blur-sm rounded-full text-primary text-sm font-medium mb-6 border border-primary/20"
              variants={fadeInDown}
            >
              <Icon name="Smartphone" size={16} className="mr-2" />
              Download the Mobile App
            </motion.div>

            <motion.h1
              className="text-4xl sm:text-5xl lg:text-6xl font-bold text-text-primary leading-tight mb-6"
              variants={fadeInUp}
            >
              Manage Your Business{" "}
              <span className="text-primary bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent">
                On the Go
              </span>
            </motion.h1>

            <motion.p
              className="text-lg lg:text-xl text-text-secondary mb-8 max-w-2xl mx-auto lg:mx-0"
              variants={fadeInUp}
            >
              Take control of your business from anywhere with TAK Business
              Manager mobile app. Track sales, manage inventory, and monitor
              performance - all from your smartphone.
            </motion.p>

            {/* Key Benefits */}
            <div className="grid sm:grid-cols-2 gap-4 mb-8">
              <div className="flex items-center justify-center lg:justify-start">
                <Icon
                  name="Smartphone"
                  size={20}
                  className="text-success mr-3"
                />
                <span className="text-text-primary font-medium">
                  Mobile First Design
                </span>
              </div>
              <div className="flex items-center justify-center lg:justify-start">
                <Icon name="Wifi" size={20} className="text-success mr-3" />
                <span className="text-text-primary font-medium">
                  Works Offline
                </span>
              </div>
              <div className="flex items-center justify-center lg:justify-start">
                <Icon name="Zap" size={20} className="text-success mr-3" />
                <span className="text-text-primary font-medium">
                  Real-time Sync
                </span>
              </div>
              <div className="flex items-center justify-center lg:justify-start">
                <Icon name="Shield" size={20} className="text-success mr-3" />
                <span className="text-text-primary font-medium">
                  Secure & Private
                </span>
              </div>
            </div>

            {/* App Store Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start mb-8">
              <div className="inline-flex items-center justify-center px-6 py-3 bg-gray-400 text-white rounded-lg opacity-60 cursor-not-allowed">
                <Icon name="Apple" size={24} className="mr-3" />
                <div className="text-left">
                  <div className="text-xs">Coming Soon to</div>
                  <div className="text-sm font-semibold">App Store</div>
                </div>
              </div>
              <a
                href="https://play.google.com/store/apps/details?id=com.tak.business_pos"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center px-6 py-3 bg-black text-white rounded-lg hover:bg-gray-800 transition-colors"
              >
                <Icon name="Smartphone" size={24} className="mr-3" />
                <div className="text-left">
                  <div className="text-xs">Get it on</div>
                  <div className="text-sm font-semibold">Google Play</div>
                </div>
              </a>
            </div>

            {/* App Stats */}
            <div className="grid grid-cols-3 gap-6 pt-8 border-t border-gray-200">
              <div className="text-center lg:text-left">
                <div className="text-2xl lg:text-3xl font-bold text-text-primary">
                  {animatedCounts?.businesses?.toLocaleString()}+
                </div>
                <div className="text-sm text-text-secondary">App Downloads</div>
              </div>
              <div className="text-center lg:text-left">
                <div className="text-2xl lg:text-3xl font-bold text-text-primary">
                  {(animatedCounts?.rating / 10)?.toFixed(1)}/5
                </div>
                <div className="flex items-center justify-center lg:justify-start mt-1">
                  {[...Array(5)]?.map((_, i) => (
                    <Icon
                      key={i}
                      name="Star"
                      size={16}
                      className={`${
                        i < 4 ? "text-warning fill-current" : "text-gray-300"
                      }`}
                    />
                  ))}
                </div>
              </div>
              <div className="text-center lg:text-left">
                <div className="text-2xl lg:text-3xl font-bold text-text-primary">
                  99%
                </div>
                <div className="text-sm text-text-secondary">
                  User Satisfaction
                </div>
              </div>
            </div>
          </motion.div>

          {/* Right Content - Mobile App Preview */}
          <motion.div
            className="relative"
            variants={fadeInRight}
            initial="initial"
            animate="animate"
            transition={{ delay: 0.3 }}
          >
            <div className="relative mx-auto max-w-sm">
              {/* Phone Frame */}
              <div className="relative bg-gray-900 rounded-[2.5rem] p-2 shadow-2xl">
                <div className="bg-white rounded-[2rem] overflow-hidden">
                  {/* Status Bar */}
                  <div className="bg-gray-900 text-white text-xs px-6 py-2 flex justify-between items-center">
                    <span>9:41</span>
                    <div className="flex items-center space-x-1">
                      <Icon name="Signal" size={12} />
                      <Icon name="Wifi" size={12} />
                      <Icon name="Battery" size={12} />
                    </div>
                  </div>

                  {/* App Screenshot */}
                  <div className="relative h-[600px] overflow-hidden">
                    <img
                      src="/assets/images/mockup-1.png"
                      alt="TAK Business Manager App Screenshot"
                      className="w-full h-full object-cover"
                    />
                  </div>
                </div>
              </div>

              {/* Floating Elements */}
              <div className="absolute -top-4 -right-4 bg-success text-white rounded-full p-3 shadow-lg animate-bounce">
                <Icon name="TrendingUp" size={20} />
              </div>
              <div className="absolute -bottom-4 -left-4 bg-secondary text-white rounded-full p-3 shadow-lg animate-pulse">
                <Icon name="BarChart3" size={20} />
              </div>
            </div>
          </motion.div>
        </div>
      </div>
      {/* Scroll Indicator */}
      <motion.div
        className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce"
        variants={fadeInUp}
        initial="initial"
        animate="animate"
        transition={{ delay: 1.2 }}
      >
        <Icon name="ChevronDown" size={24} className="text-text-secondary" />
      </motion.div>
    </motion.section>
  );
};

export default HeroSection;
